import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../components/Login/LoginBackground.css';
import { DocumentIcon, ChartIcon, NoteIcon, MoneyIcon } from '../components/Login/LoginIcons';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    try {
      // This would be an API call in a real application
      // For now, we'll just simulate a successful password reset request
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSuccess(true);
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-gray-100 dark:bg-gray-900">
      {/* Background with blurred corners */}
      <div className="login-background">
        <div 
          className="login-background-image"
          style={{ 
            backgroundImage: 'url(/login-bg.png)'
          }}
        />
        <div className="login-background-overlay dark:opacity-80" />
      </div>

      {/* Floating icons */}
      <div className="login-floating-icons">
        <div className="login-icon bg-blue-200">
          <div className="text-blue-800"><DocumentIcon /></div>
        </div>
        <div className="login-icon bg-red-200">
          <div className="text-red-800"><ChartIcon /></div>
        </div>
        <div className="login-icon bg-yellow-200">
          <div className="text-yellow-800"><NoteIcon /></div>
        </div>
        <div className="login-icon bg-green-200">
          <div className="text-green-800"><MoneyIcon /></div>
        </div>
      </div>

      {/* Forgot Password form */}
      <div className="login-form-container">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Reset Password</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {!isSuccess ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                placeholder="ali.mahan@example.com"
                required
              />
            </div>

            {error && (
              <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-primary hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Reset Link'}
            </button>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-primary hover:text-primary-dark">
                Back to Login
              </Link>
            </div>
          </form>
        ) : (
          <div className="text-center py-4">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Check your email</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              We've sent a password reset link to <span className="font-medium">{email}</span>
            </p>
            <Link 
              to="/login"
              className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
